import {
  applicableDateFormats,
  defaultDateFormat,
} from '../constants/commonConstants'
import {
  formatDate,
  parseDate,
  parseISODate,
  addDays,
  addMonths,
  getDifferenceAsYears,
  getStartOfISOWeek,
  getEachDayOfInterval,
  getEachMonthOfInterval,
  isSameDayDate,
  setTime,
  fromUnixTimestamp,
  getStartOfDay,
  convertMomentFormatToDateFns,
} from './dateAdapter'
import {formatInTimeZone} from './timezoneAdapter'

/**
 * compare dates and returns whether date1 is greater than date2
 *
 * @param {String or Date}   date1    date
 * @param {String or Date}   date2    date
 * @returns {boolean}  date1 > date2
 */
export const compareDates = (date1: any, date2: any) => {
  // Hardcoded to YYYY-MM-DD as of now. But will change to user locale date format
  const dateFormat = 'yyyy-MM-dd'
  if (date1 instanceof Date) {
    date1 = formatDate(date1, dateFormat)
  }
  if (date2 instanceof Date) {
    date2 = formatDate(date2, dateFormat)
  }
  const parsedDate1 = parseDate(date1, dateFormat, true)
  const parsedDate2 = parseDate(date2, dateFormat, true)
  return parsedDate1 > parsedDate2
}

/**
 * gets the modified date format for MUI Pickers
 * e.g. DD.MM.YYYY will be used as dd.MM.yyyy with MUI Pickers
 *
 * @param {String}   dateFormat    format to be used
 * @returns {String}  format returned to be used with MUI Pickets
 */
export const getModifiedDateFormatForMUI = (dateFormat = defaultDateFormat) => {
  return dateFormat.replace(/[D]/g, 'd').replace(/[Y]/g, 'y')
}

/**
 * gets the masking data for MUI Pickers to be used for keyboard input
 * e.g. for DD.MM.YYYY the masking is [/\d/ , /\d/ , ., /\d/ ,/\d/, ., /\d/, /\d/, /\d/, /\d/]
 *
 * @param {String}   dateFormat    format to be used
 * @returns {String[]}  masking array to be used with MUI Pickers
 */
export const getMaskForKeyboardInput = (dataFormat = defaultDateFormat) => {
  const maskForMUI = []
  for (const eachChar of dataFormat) {
    if (applicableDateFormats.includes(eachChar)) {
      maskForMUI.push(/\d/)
    } else {
      maskForMUI.push('.')
    }
  }
  return maskForMUI
}

/**
 * get the default date format for the user loggedin
 *
 * @returns {String}  dateformat taken from Session storage or default one
 */
export const getDateFormat = () => {
  return defaultDateFormat // Force date format for now, fix planned  on SXBOOK-5882
}

/**
 * get the difference between two dates in years
 *
 * @param {String}   date1  first date
 * @param {String}   date2  second date
 * @param {String}   dateFormat  dateFormat as string
 * @returns {String}  difference between date1 and date2 in years
 */
export const getDifferenceInDateInYears = (
  date1: string,
  date2: string,
  dateFormat: string
) => {
  dateFormat = dateFormat || getDateFormat()
  const dateFnsFormat = convertMomentFormatToDateFns(dateFormat)
  const parsedDate1 = date1 ? parseDate(date1, dateFnsFormat, true) : new Date()
  const parsedDate2 = date2 ? parseDate(date2, dateFnsFormat, true) : new Date()
  return getDifferenceAsYears(parsedDate1, parsedDate2)
}

/**
 * get the difference between multiple dates as years
 *
 * @param {Array[] Object}     objList List of objects
 * @param {String}   validFromAPI  firstDateParam
 * @param {String}   validToAPI  secondDateParam
 * @param {String}   dateFormat  dateFormat
 * @returns {Integer} difference from the ranges
 */
export const getRangesFromOverlappingDates = (
  objList: any[],
  validFromAPI: string,
  validToAPI: string,
  dateFormat: string
) => {
  const monthYearSet = new Set()
  const dateFnsFormat = convertMomentFormatToDateFns(dateFormat)

  objList.forEach((obj: any) => {
    const startDate = obj[validFromAPI]
      ? parseDate(obj[validFromAPI], dateFnsFormat, true)
      : new Date()
    const endDate = obj[validToAPI]
      ? parseDate(obj[validToAPI], dateFnsFormat, true)
      : new Date()

    const months = getEachMonthOfInterval(startDate, endDate)
    months.forEach(month => {
      monthYearSet.add(formatDate(month, 'yyyy-MM'))
    })
  })

  return monthYearSet.size / 12
}

/**
 * get the year and month from passed in numerical value
 *
 * @param {number}   numericalValue  number to be parsed to year and month
 * @returns {String}  response in '{yearValue} Year(s) and ${monthValue} month(s)'
 */
export const getYearMonthFromNumber = (numericalValue: number) => {
  const yearValue = Math.floor(numericalValue)
  let monthValue = numericalValue - Math.floor(numericalValue)
  monthValue = Math.round(monthValue * 12)
  let yearMonth = ''
  if (yearValue) {
    yearMonth = `${yearValue} Year(s)`
    if (monthValue) {
      yearMonth += ` and ${monthValue} month(s)`
    }
  } else if (monthValue) {
    yearMonth = `${monthValue} month(s)`
  } else {
    yearMonth = 'Less than 1 month'
  }
  return yearMonth
}

/**
 * get the list of days in a week
 */
export const getWeekDays = () => {
  const startOfWeek = getStartOfISOWeek(new Date())
  const endOfWeek = addDays(startOfWeek, 4)

  return getEachDayOfInterval(startOfWeek, endOfWeek)
}

export const convertUnixEpoc = (epocTime: any) => {
  const date = fromUnixTimestamp(epocTime)
  return formatDate(date, 'dd.MM.yyyy HH:mm')
}

export const isVotingApplicable = (selectedDate: any) => {
  const todayDate = new Date()
  const todayDateFormatted = formatDate(todayDate, 'yyyy-MM-dd HH:mm')
  const today1pmDate = formatInTimeZone(
    setTime(todayDate, {hours: 13, minutes: 0}),
    'Europe/Berlin',
    'yyyy-MM-dd HH:mm'
  )
  const today1pmDateParsed = parseDate(today1pmDate, 'yyyy-MM-dd HH:mm')

  const yesterdayDate = addDays(todayDate, -1)
  const dateFnsFormat = convertMomentFormatToDateFns(getDateFormat())
  const menuDate = parseDate(selectedDate, dateFnsFormat, true)

  const todayDateParsed = parseDate(todayDateFormatted, 'yyyy-MM-dd HH:mm')
  const isValid =
    (isSameDayDate(menuDate, yesterdayDate) &&
      todayDateParsed < today1pmDateParsed) ||
    (isSameDayDate(menuDate, todayDate) &&
      todayDateParsed >= today1pmDateParsed)
  return isValid
}

export const compareTwoDates = (date1: string, date2: string) => {
  if (!date1 || !date2) {
    return false
  }
  const date1Obj = getStartOfDay(new Date(date1))
  const date2Obj = getStartOfDay(new Date(date2))
  return (
    formatDate(date1Obj, "yyyy-MM-dd'T'HH:mm:ssXXX") >
    formatDate(date2Obj, "yyyy-MM-dd'T'HH:mm:ssXXX")
  )
}

export const formatDateTimeFromString = (dateString: string = '') => {
  if (!dateString) {
    return ''
  }
  const dateFormat = getDateFormat()
  const dateFnsFormat = convertMomentFormatToDateFns(dateFormat)
  const parsedDate = parseDate(dateString, "yyyy-MM-dd'T'HH:mm:ss")
  const weekdayFormat = 'EEE ' + dateFnsFormat + ', HH:mm'
  const newDate = formatDate(parsedDate, weekdayFormat)
  return newDate
}
